import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { securityAPI } from '../../services/api';
import { toast } from 'react-hot-toast';
import DashboardLayout from '../../components/layout/DashboardLayout';
import useDocumentTitle from '../../hooks/useDocumentTitle';

interface SecurityIssue {
  id: number;
  url: string;
  issue_type: string;
  severity: string;
  matched_pattern: string;
  matched_content: string;
  status: string;
  detected_at: string;
  notes?: string;
}

const SecurityVulnerabilitiesPage: React.FC = () => {
  useDocumentTitle('Vulnerabilities');
  const { currentAsset } = useAuth();
  const [loading, setLoading] = useState(true);
  const [issues, setIssues] = useState<SecurityIssue[]>([]);
  const [filteredIssues, setFilteredIssues] = useState<SecurityIssue[]>([]);
  const [selectedIssue, setSelectedIssue] = useState<SecurityIssue | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [filters, setFilters] = useState({
    status: '',
    severity: '',
    search: '',
  });
  const [updateForm, setUpdateForm] = useState({
    status: '',
    notes: '',
  });

  useEffect(() => {
    if (currentAsset) {
      loadIssues();
    }
  }, [currentAsset]);

  useEffect(() => {
    applyFilters();
  }, [issues, filters]);

  const loadIssues = async () => {
    if (!currentAsset) return;

    setLoading(true);
    try {
      const response = await securityAPI.getLogs(currentAsset.id, { days: 90 });
      if (response.data.status === 'success') {
        setIssues(response.data.data.issues || []);
      }
    } catch (error: any) {
      // Don't log auth errors - API interceptor handles those
      if (error.response?.status !== 401) {
        console.error('Failed to load security issues:', error);
        toast.error('Failed to load security issues');
      }
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...issues];

    if (filters.status) {
      filtered = filtered.filter(i => i.status === filters.status);
    }

    if (filters.severity) {
      filtered = filtered.filter(i => i.severity === filters.severity);
    }

    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(i =>
        i.url.toLowerCase().includes(searchLower) ||
        i.issue_type.toLowerCase().includes(searchLower) ||
        i.matched_content.toLowerCase().includes(searchLower)
      );
    }

    setFilteredIssues(filtered);
  };

  const handleUpdateIssue = async () => {
    if (!selectedIssue) return;

    try {
      await securityAPI.updateLog(selectedIssue.id, {
        status: updateForm.status,
        notes: updateForm.notes,
      });

      toast.success('Issue updated successfully');
      setShowModal(false);
      setSelectedIssue(null);
      loadIssues();
    } catch (error: any) {
      toast.error('Failed to update issue');
    }
  };

  const openUpdateModal = (issue: SecurityIssue) => {
    setSelectedIssue(issue);
    setUpdateForm({
      status: issue.status,
      notes: issue.notes || '',
    });
    setShowModal(true);
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'danger';
      case 'high': return 'warning';
      case 'medium': return 'info';
      case 'low': return 'secondary';
      default: return 'secondary';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open': return 'danger';
      case 'investigating': return 'warning';
      case 'false_positive': return 'secondary';
      case 'resolved': return 'success';
      default: return 'secondary';
    }
  };

  const getIssueTypeIcon = (type: string) => {
    switch (type) {
      case 'sql_injection': return 'bi-database-exclamation';
      case 'xss_vulnerability': return 'bi-code-slash';
      case 'sensitive_data_exposure': return 'bi-eye';
      case 'directory_listing': return 'bi-folder2-open';
      case 'suspicious_code': return 'bi-bug';
      default: return 'bi-shield-exclamation';
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <div className="text-center">
            <div className="spinner-border text-primary mb-3" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="text-muted">Loading vulnerabilities...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="container-fluid">
        {/* Header */}
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h1 className="h3 mb-1">Security Vulnerabilities</h1>
            <p className="text-muted mb-0">View and manage detected security issues</p>
          </div>
          <button className="btn btn-outline-primary" onClick={loadIssues}>
            <i className="bi bi-arrow-clockwise me-2"></i>Refresh
          </button>
        </div>

        {/* Filters */}
        <div className="card border-0 shadow-sm mb-4">
          <div className="card-body">
            <div className="row g-3">
              <div className="col-md-4">
                <div className="input-group">
                  <span className="input-group-text bg-white">
                    <i className="bi bi-search"></i>
                  </span>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search by URL, type, or content..."
                    value={filters.search}
                    onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  />
                </div>
              </div>
              <div className="col-md-3">
                <select
                  className="form-select"
                  value={filters.severity}
                  onChange={(e) => setFilters({ ...filters, severity: e.target.value })}
                >
                  <option value="">All Severities</option>
                  <option value="critical">Critical</option>
                  <option value="high">High</option>
                  <option value="medium">Medium</option>
                  <option value="low">Low</option>
                </select>
              </div>
              <div className="col-md-3">
                <select
                  className="form-select"
                  value={filters.status}
                  onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                >
                  <option value="">All Statuses</option>
                  <option value="open">Open</option>
                  <option value="investigating">Investigating</option>
                  <option value="false_positive">False Positive</option>
                  <option value="resolved">Resolved</option>
                </select>
              </div>
              <div className="col-md-2">
                <button
                  className="btn btn-outline-secondary w-100"
                  onClick={() => setFilters({ status: '', severity: '', search: '' })}
                >
                  Clear Filters
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Issues List */}
        <div className="card border-0 shadow-sm">
          <div className="card-header bg-white border-bottom py-3">
            <div className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0">
                {filteredIssues.length} {filteredIssues.length === 1 ? 'Issue' : 'Issues'} Found
              </h5>
              <div className="d-flex gap-2">
                <span className="badge bg-danger">{issues.filter(i => i.status === 'open').length} Open</span>
                <span className="badge bg-warning">{issues.filter(i => i.status === 'investigating').length} Investigating</span>
                <span className="badge bg-success">{issues.filter(i => i.status === 'resolved').length} Resolved</span>
              </div>
            </div>
          </div>
          <div className="card-body p-0">
            {filteredIssues.length > 0 ? (
              <div className="list-group list-group-flush">
                {filteredIssues.map((issue) => (
                  <div key={issue.id} className="list-group-item px-4 py-3">
                    <div className="row align-items-center">
                      <div className="col-auto">
                        <div className={`rounded-circle bg-${getSeverityColor(issue.severity)} bg-opacity-10 d-flex align-items-center justify-content-center`} style={{ width: '48px', height: '48px' }}>
                          <i className={`bi ${getIssueTypeIcon(issue.issue_type)} text-${getSeverityColor(issue.severity)} fs-5`}></i>
                        </div>
                      </div>
                      <div className="col">
                        <div className="d-flex align-items-center gap-2 mb-1">
                          <h6 className="mb-0 text-capitalize">{issue.issue_type.replace(/_/g, ' ')}</h6>
                          <span className={`badge bg-${getSeverityColor(issue.severity)}`}>
                            {issue.severity.toUpperCase()}
                          </span>
                          <span className={`badge bg-${getStatusColor(issue.status)}`}>
                            {issue.status.replace(/_/g, ' ')}
                          </span>
                        </div>
                        <div className="mb-2">
                          <code className="bg-light px-2 py-1 rounded small">{issue.url}</code>
                        </div>
                        <div className="text-muted small">
                          <strong>Pattern:</strong> <code>{issue.matched_pattern}</code>
                        </div>
                        <div className="text-muted small mt-1">
                          <strong>Match:</strong> <code className="text-danger">{issue.matched_content.substring(0, 100)}{issue.matched_content.length > 100 ? '...' : ''}</code>
                        </div>
                      </div>
                      <div className="col-auto text-end">
                        <div className="text-muted small mb-2">
                          {new Date(issue.detected_at).toLocaleDateString()}
                        </div>
                        <button
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => openUpdateModal(issue)}
                        >
                          <i className="bi bi-pencil me-1"></i>Update
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-5">
                <i className="bi bi-shield-check fs-1 text-success mb-3 d-block"></i>
                <h5>No Issues Found</h5>
                <p className="text-muted mb-0">
                  {issues.length > 0 ? 'No issues match your current filters' : 'No security vulnerabilities detected'}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Update Modal */}
      {showModal && selectedIssue && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Update Issue Status</h5>
                <button type="button" className="btn-close" onClick={() => setShowModal(false)}></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">Issue</label>
                  <div className="bg-light p-3 rounded">
                    <div className="fw-semibold text-capitalize mb-1">
                      {selectedIssue.issue_type.replace(/_/g, ' ')}
                    </div>
                    <code className="small">{selectedIssue.url}</code>
                  </div>
                </div>
                <div className="mb-3">
                  <label className="form-label">Status</label>
                  <select
                    className="form-select"
                    value={updateForm.status}
                    onChange={(e) => setUpdateForm({ ...updateForm, status: e.target.value })}
                  >
                    <option value="open">Open</option>
                    <option value="investigating">Investigating</option>
                    <option value="false_positive">False Positive</option>
                    <option value="resolved">Resolved</option>
                  </select>
                </div>
                <div className="mb-3">
                  <label className="form-label">Notes</label>
                  <textarea
                    className="form-control"
                    rows={3}
                    placeholder="Add resolution notes..."
                    value={updateForm.notes}
                    onChange={(e) => setUpdateForm({ ...updateForm, notes: e.target.value })}
                  ></textarea>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button type="button" className="btn btn-primary" onClick={handleUpdateIssue}>
                  Update Issue
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default SecurityVulnerabilitiesPage;
